import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result } from 'antd';
import { FormattedMessage } from 'react-intl';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render-time errors anywhere below it and shows a friendly fallback
 * instead of a blank white screen, with a button to reload the app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // There is no client-side logging/telemetry infrastructure yet; the
    // fallback UI below is what the user sees. Wire a reporter here later.
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Result
          status='500'
          title={<FormattedMessage id='error-boundary-title' />}
          subTitle={<FormattedMessage id='error-boundary-subtitle' />}
          extra={
            <Button type='primary' onClick={this.handleReload}>
              <FormattedMessage id='error-boundary-reload' />
            </Button>
          }
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
